import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { setError } from "../redux/slices/errorSlice";
import {
  incrementLoading,
  decrementLoading,
} from "../redux/slices/loadingSlice";
import {
  setAccessToken,
  loginSuccess,
  logout,
  setInitialized,
} from "../redux/slices/authSlice";

const isLocalHost = ["localhost", "127.0.0.1"].includes(
  window.location.hostname
);
const api_url =
  process.env.REACT_APP_API_URL ||
  (isLocalHost
    ? "http://localhost:8000/api/"
    : "https://kmch-backend-withdb-gbade0afath6gdct.ukwest-01.azurewebsites.net/api/");
// Create an instance of axios
const axiosInstance = axios.create({
  baseURL: api_url,
  timeout: 60000, // 60 seconds
  withCredentials: true,
});

// Variable to hold the Redux store
let store;
export const injectStore = (_store) => {
  store = _store;
};

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    if (store) {
      const state = store.getState();
      const accessToken = state.auth.accessToken;
      if (accessToken) {
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      store.dispatch(incrementLoading());
    }
    return config;
  },
  (error) => {
    if (store) {
      store.dispatch(decrementLoading());
    }
    return Promise.reject(error);
  }
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (store) {
      store.dispatch(decrementLoading());
    }
    return response;
  },
  async (error) => {
    if (store) {
      store.dispatch(decrementLoading());
    }
    const originalRequest = error.config;
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      // Check if the failed request is the token refresh request
      if (originalRequest.url.includes("token/refresh/")) {
        // If the refresh token is invalid, log the user out
        if (store) {
          store.dispatch(logout());
          store.dispatch(setInitialized());
        }
        return Promise.reject(error);
      }
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const refreshResponse = await axiosInstance.post("token/refresh/");
        const { access } = refreshResponse.data;

        if (!access) {
          // If no access token is returned, log the user out
          if (store) {
            store.dispatch(logout());
            store.dispatch(setInitialized());
          }
          return Promise.reject(error);
        }

        // Update access token in Redux store
        store.dispatch(setAccessToken(access));

        // Decode the access token to get is_admin
        const decodedToken = jwtDecode(access);
        const isAdmin = decodedToken.is_admin;

        store.dispatch(loginSuccess({ is_admin: isAdmin }));

        // Update the Authorization header and retry the original request
        originalRequest.headers["Authorization"] = `Bearer ${access}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        if (store) {
          store.dispatch(logout());
          store.dispatch(setInitialized());
        }
        return Promise.reject(refreshError);
      }
    } else {
      // For all other errors, dispatch the setError action
      // only dispatch if caller didn’t opt-out
      if (store && !originalRequest.skipGlobalError) {
        const errorMessage = error.response?.data?.message || error.message;
        store.dispatch(setError(errorMessage));
      }
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
